import { PaginatedList, PaginationParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

export enum IntegrationType {
  Samsara = 1,
  EvaEld = 2,
  GPSTracking = 3,
  AnyTrack = 4,
  Motive = 5,
  Other = 99,
}

export interface IntegrationAccountDto {
  id: number;
  tenantId: number;
  integrationType: IntegrationType;
  integrationTypeName: string;
  accountName: string;
  apiKey: string; // Masked for security
  additionalConfig?: string | null;
  isActive: boolean;
  lastSyncedAt?: string | null;
  lastSyncStatus?: string | null;
  lastSyncError?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateIntegrationAccountDto {
  integrationType: IntegrationType;
  accountName: string;
  apiKey: string;
  additionalConfig?: string | null;
  isActive?: boolean;
}

export interface UpdateIntegrationAccountDto {
  integrationType: IntegrationType;
  accountName: string;
  apiKey: string;
  additionalConfig?: string | null;
  isActive: boolean;
}

const integrationAccountsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get paginated integration accounts
    getIntegrationAccounts: builder.query<
      PaginatedList<IntegrationAccountDto>,
      PaginationParams
    >({
      query: (params) => ({
        url: "/integrationaccounts",
        params,
      }),
      transformResponse: unwrapApiResponse<PaginatedList<IntegrationAccountDto>>,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "IntegrationAccount" as const,
                id,
              })),
              { type: "IntegrationAccount", id: "LIST" },
            ]
          : [{ type: "IntegrationAccount", id: "LIST" }],
    }),

    // Get integration account by ID
    getIntegrationAccountById: builder.query<IntegrationAccountDto, number>({
      query: (id) => `/integrationaccounts/${id}`,
      transformResponse: unwrapApiResponse<IntegrationAccountDto>,
      providesTags: (result, error, id) => [
        { type: "IntegrationAccount", id },
      ],
    }),

    // Create integration account
    createIntegrationAccount: builder.mutation<
      IntegrationAccountDto,
      CreateIntegrationAccountDto
    >({
      query: (body) => ({
        url: "/integrationaccounts",
        method: "POST",
        body,
      }),
      transformResponse: unwrapApiResponse<IntegrationAccountDto>,
      invalidatesTags: [{ type: "IntegrationAccount", id: "LIST" }],
    }),

    // Update integration account
    updateIntegrationAccount: builder.mutation<
      IntegrationAccountDto,
      { id: number; data: UpdateIntegrationAccountDto }
    >({
      query: ({ id, data }) => ({
        url: `/integrationaccounts/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: unwrapApiResponse<IntegrationAccountDto>,
      invalidatesTags: (result, error, { id }) => [
        { type: "IntegrationAccount", id },
        { type: "IntegrationAccount", id: "LIST" },
      ],
    }),

    // Delete integration account
    deleteIntegrationAccount: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `/integrationaccounts/${id}`,
        method: "DELETE",
      }),
      transformResponse: unwrapApiResponse<boolean>,
      invalidatesTags: (result, error, id) => [
        { type: "IntegrationAccount", id },
        { type: "IntegrationAccount", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetIntegrationAccountsQuery,
  useGetIntegrationAccountByIdQuery,
  useCreateIntegrationAccountMutation,
  useUpdateIntegrationAccountMutation,
  useDeleteIntegrationAccountMutation,
} = integrationAccountsApi;

export default integrationAccountsApi;

